/**
 * Leadler › Harita, world view: every country coloured by its place in the scan plan
 * (config/regions.yaml), one marker per region with its numbers, and the plan card under
 * it (waves, progress per region, the weekly quota and the next cells). Tapping a region
 * opens its cell map below; a region without a grid yet says so. Company-level data only.
 */

import { useMemo } from 'react';
import type { LeadMapCell, LeadsView, WorldCountry, WorldRegion } from '../api/leads.js';
import { formatNumber, formatRelativeDay } from '../lib/format.js';
import { WORLD_HEIGHT, WORLD_WIDTH, world } from '../lib/world-geo.js';
import { SectionHeader } from './primitives.js';

export type RegionState = 'done' | 'scanning' | 'queued' | 'planned' | 'off';

export const REGION_STATE_LABELS: Readonly<Record<RegionState, string>> = Object.freeze({
  done: 'tamamlandı',
  scanning: 'taranıyor',
  queued: 'sırada',
  planned: 'planlı',
  off: 'kapalı',
});

/** done: every cell covered; scanning: work started; queued: grid exists, nothing scanned; planned: no grid yet. */
export function regionState(region: WorldRegion): RegionState {
  if (!region.enabled) return 'off';
  if (region.cells === 0) return 'planned';
  if (region.scanned + region.partial >= region.cells && region.scanning === 0) return 'done';
  if (region.scanned + region.partial + region.scanning > 0) return 'scanning';
  return 'queued';
}

function regionSummary(region: WorldRegion, state: RegionState): string {
  const parts = [`${region.code} ${region.name}`, `dalga ${region.wave}`, REGION_STATE_LABELS[state]];
  if (region.cells > 0) parts.push(`${region.scanned}/${region.cells} hücre tarandı`);
  if (region.leads > 0) parts.push(`${region.leads} firma`, `${region.qualified} nitelikli`);
  return parts.join(', ');
}

function byPlan(a: WorldRegion, b: WorldRegion): number {
  return a.wave - b.wave || a.priority - b.priority || a.code.localeCompare(b.code);
}

export function WorldMap({
  view,
  selected,
  onSelect,
}: {
  view: LeadsView;
  selected: string | null;
  onSelect: (code: string) => void;
}) {
  const geo = useMemo(() => world(), []);
  const regions = [...view.world.regions].sort(byPlan);
  const byCountry = new Map<string, WorldCountry>(view.world.countries.map((country) => [country.code, country]));
  const regionOf = new Map<string, WorldRegion>();
  for (const region of regions) for (const country of region.countries) regionOf.set(country, region);
  const stateOfCountry = (alpha2: string): RegionState | 'none' => {
    const region = regionOf.get(alpha2);
    if (region === undefined) return 'none';
    return regionState(region);
  };
  const counts = new Map<RegionState, number>();
  for (const region of regions) {
    const state = regionState(region);
    counts.set(state, (counts.get(state) ?? 0) + 1);
  }
  const label = `Dünya haritası: ${regions.length} bölge, ${counts.get('done') ?? 0} tamamlandı, ${counts.get('scanning') ?? 0} taranıyor, ${counts.get('queued') ?? 0} sırada, ${counts.get('planned') ?? 0} planlı`;
  return (
    <section className="lr-block wm" aria-labelledby="wm-title">
      <SectionHeader
        id="wm-title"
        title="Dünya haritası"
        right={<span className="section-header__aside">dalga sırasıyla</span>}
      />
      {/* biome-ignore lint/a11y/useSemanticElements: an SVG map; the markers inside it are the controls. */}
      <svg
        className="wm__svg"
        viewBox={`0 0 ${WORLD_WIDTH} ${WORLD_HEIGHT}`}
        role="group"
        aria-label={label}
        preserveAspectRatio="xMidYMid meet"
      >
        <path className="wm__sphere" d={geo.outline} />
        {geo.shapes.map((shape) => (
          // Some Natural Earth shapes share a code (disputed areas, -99): the name tells them apart.
          <path
            key={`${shape.alpha2}:${shape.name}`}
            className={`wm__country wm__country--${stateOfCountry(shape.alpha2)}`}
            d={shape.d}
          >
            <title>
              {shape.name}
              {byCountry.get(shape.alpha2)?.orders ? ` · ${byCountry.get(shape.alpha2)?.orders} sipariş` : ''}
            </title>
          </path>
        ))}
        {regions.map((region) => {
          const at = region.labelAt === null ? null : geo.project(region.labelAt.lat, region.labelAt.lon);
          const fallback = geo.shapes.find((shape) => shape.alpha2 === region.countries[0])?.centroid ?? null;
          const point = at ?? fallback;
          if (point === null) return null;
          const state = regionState(region);
          const isSelected = selected === region.code;
          const radius = region.cells === 0 ? 5 : Math.min(14, 6 + Math.sqrt(region.cells));
          return (
            // biome-ignore lint/a11y/useSemanticElements: an SVG marker; a button element cannot live inside <svg>.
            <g
              key={region.code}
              className={`wm__marker wm__marker--${state}${isSelected ? ' wm__marker--selected' : ''}`}
              transform={`translate(${point[0].toFixed(1)} ${point[1].toFixed(1)})`}
              role="button"
              tabIndex={0}
              aria-pressed={isSelected}
              aria-label={regionSummary(region, state)}
              onClick={() => onSelect(region.code)}
              onKeyDown={(event) => {
                if (event.key === 'Enter' || event.key === ' ') {
                  event.preventDefault();
                  onSelect(region.code);
                }
              }}
            >
              <circle r={radius + 3} className="wm__halo" />
              <circle r={radius} className="wm__dot" />
              <text className="wm__code" y={radius + 11} textAnchor="middle">
                {region.code}
              </text>
              {region.leads > 0 ? (
                <text className="wm__n" y={radius + 21} textAnchor="middle">
                  {formatNumber(region.leads)} firma
                </text>
              ) : null}
            </g>
          );
        })}
      </svg>
      <ul className="wm__legend" aria-label="Harita durumları">
        {(['done', 'scanning', 'queued', 'planned'] as const).map((state) => (
          <li key={state} className={`wm__legend-item wm__legend-item--${state}`}>
            <span className="wm__swatch" aria-hidden="true" />
            {REGION_STATE_LABELS[state]} <span className="mono">{counts.get(state) ?? 0}</span>
          </li>
        ))}
        <li className="wm__legend-item wm__legend-item--none">
          <span className="wm__swatch" aria-hidden="true" />
          plan dışı
        </li>
      </ul>
      <p className="wm__source">
        Harita: Natural Earth 1:110m (world-atlas), çevrimdışı. Bölgeler: config/regions.yaml.
      </p>
    </section>
  );
}

/** The next cells of the plan across regions: wave, priority, then the cell's own order. */
export function nextCells(view: LeadsView, limit = 10): { cell: LeadMapCell; region: WorldRegion }[] {
  const regions = new Map(view.world.regions.map((region) => [region.code, region]));
  return view.cells
    .filter((cell) => cell.status === 'taranmadi' && cell.plannedOrder !== null && cell.region !== null)
    .flatMap((cell) => {
      const region = regions.get(cell.region ?? '');
      return region === undefined || !region.enabled ? [] : [{ cell, region }];
    })
    .sort((a, b) => byPlan(a.region, b.region) || (a.cell.plannedOrder ?? 0) - (b.cell.plannedOrder ?? 0))
    .slice(0, limit);
}

export function ScanPlan({ view, now, onSelect }: { view: LeadsView; now: number; onSelect: (code: string) => void }) {
  const regions = [...view.world.regions].sort(byPlan);
  const waves = [...new Set(regions.map((region) => region.wave))].sort((a, b) => a - b);
  const totalCells = regions.reduce((sum, region) => sum + region.cells, 0);
  const scanned = regions.reduce((sum, region) => sum + region.scanned, 0);
  const quota = view.world.weeklyCellQuota;
  const weeklyCost = quota * view.world.costPerCellUsd;
  const upcoming = nextCells(view);
  return (
    <section className="lr-block wm-plan" aria-labelledby="wm-plan-title">
      <SectionHeader
        id="wm-plan-title"
        title="Tarama planı"
        right={
          <span className="section-header__aside">
            <span className="mono">{scanned}</span>/<span className="mono">{totalCells}</span> hücre
          </span>
        }
      />
      <p className="wm-plan__quota">
        Haftalık kota <span className="mono">{quota}</span> hücre · tahmini{' '}
        <span className="mono">{formatNumber(weeklyCost, 2)} $</span>/hafta (hücre başına{' '}
        <span className="mono">{formatNumber(view.world.costPerCellUsd, 2)} $</span>) · tamamlanan hücre{' '}
        <span className="mono">{view.world.rescanDays}</span> gün sonra yeniden taranır
      </p>
      {waves.map((wave) => (
        <div key={wave} className="wm-wave">
          <h3 className="wm-wave__title">Dalga {wave}</h3>
          <ol className="wm-wave__list" aria-label={`Dalga ${wave} bölgeleri`}>
            {regions
              .filter((region) => region.wave === wave)
              .map((region) => {
                const state = regionState(region);
                const pct =
                  region.cells === 0 ? 0 : Math.round(((region.scanned + region.partial) / region.cells) * 100);
                return (
                  <li key={region.code} className={`wm-region wm-region--${state}`}>
                    <button
                      type="button"
                      className="wm-region__main"
                      onClick={() => onSelect(region.code)}
                      aria-label={`${region.code} ${region.name}: ${REGION_STATE_LABELS[state]}, aç`}
                    >
                      <span className="wm-region__code mono">{region.code}</span>
                      <span className="wm-region__name">{region.name}</span>
                      <span className={`wm-region__state wm-region__state--${state}`}>
                        {REGION_STATE_LABELS[state]}
                      </span>
                    </button>
                    <div className="wm-region__bar" aria-hidden="true">
                      <span className="wm-region__fill" style={{ width: `${pct}%` }} />
                    </div>
                    <span className="wm-region__nums">
                      {region.cells > 0 ? (
                        <>
                          <span className="mono">
                            {region.scanned}/{region.cells}
                          </span>{' '}
                          hücre · <span className="mono">{region.leads}</span> firma ·{' '}
                          <span className="mono">{region.qualified}</span> nitelikli
                          {region.lastScannedAt !== null ? (
                            <>
                              {' '}
                              · son <span className="mono">{formatRelativeDay(region.lastScannedAt, now)}</span>
                            </>
                          ) : null}
                        </>
                      ) : (
                        <>hücre ızgarası henüz üretilmedi · {region.countries.join(', ')}</>
                      )}
                      {region.note ? <span className="wm-region__note"> · {region.note}</span> : null}
                    </span>
                  </li>
                );
              })}
          </ol>
        </div>
      ))}
      <h3 className="wm-wave__title">Sıradaki hücreler</h3>
      {upcoming.length === 0 ? (
        <p className="wm-plan__empty">Sırada hücre yok: sıradaki bölge için ızgara üretilince burada listelenir.</p>
      ) : (
        <ol className="wm-next" aria-label="Sıradaki hücreler">
          {upcoming.map(({ cell, region }) => (
            <li key={cell.cellPk} className="wm-next__item">
              <span className="mono">{region.code}</span> · {cell.localities[0] ?? cell.cellId} ·{' '}
              <span className="mono">{cell.cellId}</span>
              {cell.plannedOrder !== null ? <span className="ink-3"> · sıra {cell.plannedOrder}</span> : null}
            </li>
          ))}
        </ol>
      )}
    </section>
  );
}
